import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/site/kit";
import { Reveal } from "@/components/motion/primitives";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { sendContact, getFaqs } from "@/lib/api";
import LiveQueue from "@/components/site/LiveQueue";
import { toast } from "sonner";
import SEO, { breadcrumbSchema } from "@/components/site/SEO";
import {
  Mail, Twitter, Linkedin, Instagram, Youtube, Github, Send, MessageSquare,
  Globe, Smartphone, ShoppingBag, Truck, Search, Megaphone, Cpu,
  Users, Palette, Rocket, Sparkles, ArrowLeft,
} from "lucide-react";

const SOCIALS = [
  [Mail, "Email", "julius.a.mason@gmail.com", "mailto:julius.a.mason@gmail.com"],
  [Twitter, "Twitter / X", "@juliusmason", "https://twitter.com/juliusmason"],
  [Linkedin, "LinkedIn", "in/juliusmason", "https://linkedin.com/in/juliusmason"],
  [Instagram, "Instagram", "@juliusmason", "https://instagram.com/juliusmason"],
  [Youtube, "YouTube", "@juliusmason", "https://youtube.com/@juliusmason"],
  [Github, "GitHub", "juliusmason", "https://github.com/juliusmason"],
];

// Common tail asked on every track.
const COMMON = [
  { key: "timeline", label: "When do you need this by?", type: "select",
    options: ["ASAP", "Within a month", "1–3 months", "3–6 months", "Just exploring"] },
  { key: "budget", label: "Rough budget", type: "select",
    options: ["Under $1k", "$1–5k", "$5–15k", "$15–50k", "$50k+", "Not sure yet"] },
];

// 13 service tracks, each with tailored follow-up questions.
const SERVICES = [
  {
    key: "website", label: "Website / Landing Page", icon: Globe,
    blurb: "Marketing site, portfolio, or a high-converting landing page.",
    fields: [
      { key: "current_site", label: "Do you have a site already? (URL if yes)", type: "text" },
      { key: "pages", label: "Roughly how many pages?", type: "select", options: ["Single landing page", "3–5 pages", "5–15 pages", "15+ pages"] },
      { key: "goals", label: "What does success look like?", type: "textarea", placeholder: "e.g. book calls, capture emails, sell a product…" },
      { key: "cms", label: "Do you want to edit content yourself?", type: "select", options: ["Yes — need a CMS", "No — Julius updates it", "Not sure"] },
    ],
  },
  {
    key: "app", label: "Custom App or Software", icon: Cpu,
    blurb: "SaaS, internal tool, dashboard, mobile-web app, or MVP.",
    fields: [
      { key: "problem", label: "What problem does the app solve?", type: "textarea", required: true },
      { key: "users", label: "Who will use it and roughly how many?", type: "text" },
      { key: "platforms", label: "Where should it run?", type: "select", options: ["Web only", "Mobile web", "iOS + Android", "Desktop", "Not sure"] },
      { key: "integrations", label: "Any systems it must talk to?", type: "text", placeholder: "e.g. Stripe, Google Sheets, HubSpot…" },
    ],
  },
  {
    key: "mobile", label: "Mobile App", icon: Smartphone,
    blurb: "iOS / Android app — native or cross-platform.",
    fields: [
      { key: "concept", label: "What's the app in one sentence?", type: "textarea", required: true },
      { key: "os", label: "Target platforms", type: "select", options: ["iOS only", "Android only", "Both", "Not sure"] },
      { key: "backend_needs", label: "Does it need a backend / accounts?", type: "select", options: ["Yes", "No", "Not sure"] },
    ],
  },
  {
    key: "ecommerce", label: "E-commerce Store", icon: ShoppingBag,
    blurb: "Shopify, custom Stripe checkout, or a full store rebuild.",
    fields: [
      { key: "products", label: "What are you selling?", type: "textarea", required: true },
      { key: "catalog_size", label: "Roughly how many SKUs?", type: "select", options: ["1–10", "10–50", "50–200", "200+"] },
      { key: "platform", label: "Preferred stack", type: "select", options: ["Shopify", "Custom (React + Stripe)", "WooCommerce", "Not sure — recommend one"] },
      { key: "fulfillment", label: "Fulfillment model", type: "select", options: ["Print-on-demand", "Dropshipping", "Warehouse / self-fulfill", "Digital only"] },
    ],
  },
  {
    key: "delivery", label: "Delivery Platform Integration", icon: Truck,
    blurb: "Hook your restaurant / shop into Uber Eats, DoorDash, Deliveroo, Just Eat, etc.",
    fields: [
      { key: "business_type", label: "What kind of business?", type: "text", placeholder: "e.g. pizzeria, grocery, dark kitchen…", required: true },
      { key: "current_channels", label: "What are you on already?", type: "text", placeholder: "e.g. only walk-in, own website, one platform…" },
      { key: "target_platforms", label: "Which platforms do you want to be on?", type: "text", placeholder: "e.g. Uber Eats + DoorDash + Deliveroo" },
      { key: "pos", label: "Do you have a POS / menu system?", type: "text", placeholder: "e.g. Square, Toast, none yet" },
    ],
  },
  {
    key: "seo", label: "SEO / Content", icon: Search,
    blurb: "Rank higher on Google, publish content that actually converts.",
    fields: [
      { key: "site_url", label: "Website URL", type: "text", required: true },
      { key: "target_keywords", label: "Any target keywords / cities?", type: "text", placeholder: "e.g. plumber Austin, best CRM…" },
      { key: "current_traffic", label: "Rough monthly traffic today", type: "select", options: ["0–500", "500–5k", "5k–50k", "50k+", "Not sure"] },
    ],
  },
  {
    key: "ads", label: "Paid Ads / Marketing", icon: Megaphone,
    blurb: "Google, Meta, TikTok — end-to-end paid growth.",
    fields: [
      { key: "channels", label: "Which channels are you interested in?", type: "text", placeholder: "e.g. Google Search, Meta, TikTok…" },
      { key: "monthly_budget", label: "Monthly ad spend", type: "select", options: ["Under $500", "$500–2k", "$2–10k", "$10k+", "Not sure yet"] },
      { key: "offer", label: "What are you promoting?", type: "textarea" },
    ],
  },
  {
    key: "automation", label: "Automation / AI Workflow", icon: Sparkles,
    blurb: "Automate ops, integrate LLMs, wire up Zapier / Make / n8n / custom.",
    fields: [
      { key: "process", label: "What manual process is eating your time?", type: "textarea", required: true },
      { key: "tools", label: "Which tools are involved?", type: "text", placeholder: "e.g. Gmail, Notion, Airtable, HubSpot…" },
      { key: "volume", label: "How often does this run?", type: "select", options: ["Daily", "Weekly", "Monthly", "One-off migration"] },
    ],
  },
  {
    key: "coaching", label: "Coaching / Consulting", icon: Users,
    blurb: "Strategy sessions for founders, marketers, and creators.",
    fields: [
      { key: "focus", label: "What's the #1 thing you want to unblock?", type: "textarea", required: true },
      { key: "cadence", label: "Preferred cadence", type: "select", options: ["One session", "Weekly", "Bi-weekly", "Monthly", "Not sure"] },
    ],
  },
  {
    key: "branding", label: "Branding / Design", icon: Palette,
    blurb: "Logo, identity system, product illustrations, and full-stack brand kits.",
    fields: [
      { key: "scope", label: "What do you need designed?", type: "textarea", required: true, placeholder: "logo, full identity, apparel, packaging…" },
      { key: "existing_assets", label: "Any existing brand assets?", type: "text" },
    ],
  },
  {
    key: "local", label: "Local Business Marketing", icon: Rocket,
    blurb: "Local SEO, Google Business Profile, & lead-gen for local service businesses.",
    fields: [
      { key: "business_name", label: "Business name", type: "text", required: true },
      { key: "area", label: "Which city / area do you serve?", type: "text", placeholder: "e.g. Austin, Manchester, Toronto…" },
      { key: "clients_now", label: "How do clients find you today?", type: "textarea" },
    ],
  },
  {
    key: "other", label: "Something Else", icon: MessageSquare,
    blurb: "Not seeing your project type? Describe it in your own words.",
    fields: [
      { key: "description", label: "Tell me what you'd like to build or fix", type: "textarea", required: true },
    ],
  },
];

export default function Contact() {
  const [step, setStep] = useState(0); // 0 = pick service, 1 = brief + contact info
  const [service, setService] = useState(null);
  const [brief, setBrief] = useState({});
  const [contact, setContact] = useState({ name: "", email: "", company: "" });
  const [loading, setLoading] = useState(false);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => { getFaqs().then(setFaqs).catch(() => setFaqs([])); }, []);

  // Deep-link support — /contact?service=Website&quote=quote-abc123 pre-selects
  // the matching service track and drops the quote id into the first textarea
  // of that track so Julius sees which Instant-Quote proposal it came from.
  const [searchParams] = useSearchParams();
  // Explicit map from Instant-Quote service keys → Contact SERVICES keys.
  const IQ_TO_CONTACT = {
    website: "website",
    app: "app",
    "mobile-app": "mobile",
    seo: "seo",
    brand: "branding",
    ai: "automation",
    automation: "automation",
    consulting: "coaching",
  };
  useEffect(() => {
    const raw = (searchParams.get("service") || "").trim();
    const quoteId = searchParams.get("quote");
    if (!raw) return;
    const lower = raw.toLowerCase();
    const mappedKey = IQ_TO_CONTACT[lower];
    const match =
      SERVICES.find((s) => s.key === mappedKey) ||
      SERVICES.find((s) => s.label.toLowerCase() === lower) ||
      SERVICES.find((s) => s.key === lower) ||
      SERVICES.find((s) => s.label.toLowerCase().includes(lower));
    if (!match) return;
    setService(match);
    setStep(1);
    if (quoteId) {
      // Prefill into the first textarea field of the matched track so the
      // reference is visible to the user (and lands in Julius's inbox).
      const firstTextarea = match.fields.find((f) => f.type === "textarea");
      if (firstTextarea) {
        setBrief({ [firstTextarea.key]: `Coming from Instant Quote ${quoteId}.\n\n` });
      } else {
        setBrief({ description: `Coming from Instant Quote ${quoteId}.\n\n` });
      }
    }
  }, [searchParams]);

  const pickService = (svc) => {
    setService(svc);
    setBrief({});
    setStep(1);
    setTimeout(() => window.scrollTo({ top: 320, behavior: "smooth" }), 40);
  };

  const back = () => { setStep(0); setService(null); };

  const setBriefField = (k) => (e) => setBrief((b) => ({ ...b, [k]: e.target.value }));
  const setContactField = (k) => (e) => setContact((c) => ({ ...c, [k]: e.target.value }));

  const [confirmation, setConfirmation] = useState(null); // { position, weeks, service, name }
  const submit = async (e) => {
    e.preventDefault();
    if (!service) return;
    // Compose a human-readable "message" from the brief.
    const summary = service.fields
      .map((f) => brief[f.key] ? `${f.label}\n→ ${brief[f.key]}` : null)
      .filter(Boolean)
      .concat(COMMON.map((f) => brief[f.key] ? `${f.label}\n→ ${brief[f.key]}` : null).filter(Boolean))
      .join("\n\n");
    setLoading(true);
    try {
      const res = await sendContact({
        name: contact.name,
        email: contact.email,
        company: contact.company,
        subject: `[${service.label}] enquiry`,
        service: service.label,
        message: summary || "(no additional details)",
        brief: { ...brief },
      });
      if (res.queue_position) {
        setConfirmation({
          position: res.queue_position,
          weeks: res.est_start_weeks,
          service: service.label,
          name: contact.name.split(" ")[0] || "there",
          email: contact.email,
        });
      } else {
        toast.success(res.message || "Message sent!");
      }
      setStep(0); setService(null); setBrief({});
      setContact({ name: "", email: "", company: "" });
    } catch {
      toast.error("Could not send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact — Start Your Project with Julius Mason"
        description="Tell me about your project — website, e-commerce, SEO, or growth. Personal reply within 1–2 business days. Based in France, serving businesses worldwide."
        path="/contact"
        lang="en"
        keywords="contact web developer, hire web designer, website project enquiry, julius mason contact"
        schema={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Contact", path: "/contact" },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "Contact Julius Mason",
            url: "https://juliusmason.com/contact",
          },
        ]}
      />
      <PageHeader
        chapter="Contact / Let's Talk"
        titleLines={["Let's build", "something great."]}
        description="Tell me what you're working on — I'll tailor a plan to your project. No calls, every language welcome, replies within 1–2 business days."
      />

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-8">
            {confirmation && (
              <Reveal>
                <div
                  data-testid="queue-confirmation"
                  className="border border-border bg-card p-8 md:p-12 relative overflow-hidden"
                >
                  <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
                  <p className="overline text-primary mb-4">Queued · {confirmation.service}</p>
                  <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-4">
                    You're in, {confirmation.name}.
                  </h2>
                  <p className="text-muted-foreground text-base md:text-lg max-w-lg mb-8">
                    Your project's been received and slotted into the studio queue. I'll be in touch personally within 1–2 business days to walk through timing.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4 mb-8">
                    <LiveQueue
                      initial={confirmation.position}
                      onChange={(position, weeks) =>
                        setConfirmation((c) => (c ? { ...c, position, weeks } : c))
                      }
                    />
                    <div className="border-l-2 border-border pl-5 py-2">
                      <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                        Estimated kick-off
                      </p>
                      <p
                        key={confirmation.weeks}
                        data-testid="queue-weeks-value"
                        className="font-display text-5xl md:text-6xl mt-1 tabular-nums queue-pulse"
                      >
                        ~{confirmation.weeks}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">weeks from today</p>
                    </div>
                  </div>
                  <div className="bg-secondary/40 border border-border p-4 mb-6">
                    <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
                      Why the queue?
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      I take on a small number of clients at once so every project gets full focus. You'll get a confirmation email at <b className="text-foreground">{confirmation.email}</b> — check spam if it hasn't arrived in 5 minutes.
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid="queue-confirmation-close"
                    onClick={() => setConfirmation(null)}
                    className="font-mono text-xs uppercase tracking-widest text-primary link-underline"
                  >
                    ← Submit another enquiry
                  </button>
                </div>
              </Reveal>
            )}
            {!confirmation && step === 0 && (
              <Reveal>
                <p className="overline text-primary mb-3">Step 1 of 2</p>
                <h2 className="font-display text-3xl md:text-4xl mb-10">What do you need help with?</h2>
                <div className="grid sm:grid-cols-2 gap-3" data-testid="service-picker">
                  {SERVICES.map((svc) => {
                    const Icon = svc.icon;
                    return (
                      <button
                        key={svc.key}
                        type="button"
                        onClick={() => pickService(svc)}
                        data-testid={`service-${svc.key}`}
                        className="group text-left border border-border p-5 hover:border-primary hover:bg-primary/5 active:scale-[0.99] transition-all"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-11 h-11 flex-shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-display text-lg leading-tight group-hover:text-primary transition-colors">{svc.label}</p>
                            <p className="text-sm text-muted-foreground mt-1 leading-snug">{svc.blurb}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </Reveal>
            )}

            {!confirmation && step === 1 && service && (
              <Reveal>
                <button
                  type="button"
                  onClick={back}
                  data-testid="contact-back"
                  className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-5"
                >
                  <ArrowLeft className="w-4 h-4" /> Change service
                </button>
                <p className="overline text-primary mb-3">Step 2 of 2 · {service.label}</p>
                <h2 className="font-display text-3xl md:text-4xl mb-8">Tell me about your project</h2>

                <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                  {/* Dynamic service-specific fields */}
                  {service.fields.map((f) => (
                    <div key={f.key} className="space-y-1.5">
                      <label className="block text-sm text-muted-foreground">
                        {f.label} {f.required && <span className="text-primary">*</span>}
                      </label>
                      {f.type === "textarea" && (
                        <textarea
                          data-testid={`brief-${f.key}`}
                          required={f.required}
                          rows={4}
                          value={brief[f.key] || ""}
                          onChange={setBriefField(f.key)}
                          placeholder={f.placeholder || ""}
                          className="w-full bg-secondary px-4 py-3.5 outline-none focus:ring-1 focus:ring-primary resize-none"
                        />
                      )}
                      {f.type === "select" && (
                        <select
                          data-testid={`brief-${f.key}`}
                          value={brief[f.key] || ""}
                          onChange={setBriefField(f.key)}
                          className="w-full bg-secondary px-4 py-3.5 outline-none focus:ring-1 focus:ring-primary"
                        >
                          <option value="">Select…</option>
                          {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      )}
                      {(f.type === "text" || !f.type) && (
                        <input
                          data-testid={`brief-${f.key}`}
                          required={f.required}
                          type="text"
                          value={brief[f.key] || ""}
                          onChange={setBriefField(f.key)}
                          placeholder={f.placeholder || ""}
                          className="w-full bg-secondary px-4 py-3.5 outline-none focus:ring-1 focus:ring-primary"
                        />
                      )}
                    </div>
                  ))}

                  {/* Common tail: timeline + budget */}
                  <div className="pt-4 border-t border-border grid sm:grid-cols-2 gap-5">
                    {COMMON.map((f) => (
                      <div key={f.key} className="space-y-1.5">
                        <label className="block text-sm text-muted-foreground">{f.label}</label>
                        <select
                          data-testid={`brief-${f.key}`}
                          value={brief[f.key] || ""}
                          onChange={setBriefField(f.key)}
                          className="w-full bg-secondary px-4 py-3.5 outline-none focus:ring-1 focus:ring-primary"
                        >
                          <option value="">Select…</option>
                          {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>

                  {/* Contact info */}
                  <div className="pt-4 border-t border-border">
                    <p className="overline text-primary mb-4">Your details</p>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <input data-testid="contact-name" required value={contact.name} onChange={setContactField("name")}
                        placeholder="Your name" className="bg-secondary px-4 py-3.5 outline-none focus:ring-1 focus:ring-primary" />
                      <input data-testid="contact-email" required type="email" value={contact.email} onChange={setContactField("email")}
                        placeholder="Your email" className="bg-secondary px-4 py-3.5 outline-none focus:ring-1 focus:ring-primary" />
                    </div>
                    <input data-testid="contact-company" value={contact.company} onChange={setContactField("company")}
                      placeholder="Company (optional)" className="mt-5 w-full bg-secondary px-4 py-3.5 outline-none focus:ring-1 focus:ring-primary" />
                  </div>

                  <button data-testid="contact-submit" type="submit" disabled={loading}
                    className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 font-semibold hover:bg-primary/90 active:scale-95 transition disabled:opacity-60">
                    {loading ? "Sending…" : <>Send Enquiry <Send className="w-4 h-4" /></>}
                  </button>
                </form>
              </Reveal>
            )}
          </div>

          <div className="lg:col-span-4">
            <Reveal>
              <h2 className="font-display text-2xl mb-8">Reach out directly</h2>
              <div className="grid grid-cols-1 gap-3">
                {SOCIALS.map(([Icon, label, handle, url]) => (
                  <a key={label} href={url} target={url.startsWith("mailto") ? undefined : "_blank"} rel="noreferrer"
                    data-testid={`social-${label.split(" ")[0].toLowerCase()}`}
                    className="group flex items-center gap-3 border border-border p-4 hover:border-primary transition-colors">
                    <Icon className="w-5 h-5 text-primary" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{label}</p>
                      <p className="text-xs text-muted-foreground truncate group-hover:text-primary transition-colors">{handle}</p>
                    </div>
                  </a>
                ))}
              </div>
              <div className="mt-8 border border-primary/40 bg-primary/5 p-6" data-testid="response-guarantee">
                <p className="overline text-primary mb-2">My promise</p>
                <p className="font-display text-2xl leading-tight mb-3">A personal reply within 1–2 business days. Guaranteed.</p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Every message lands directly in my inbox — no assistants, no ticket queue, no bots.
                  You write, I read, I reply. Myself.
                </p>
              </div>
              <div className="mt-8 border border-border p-6 bg-secondary/30">
                <p className="overline text-primary mb-2">Note</p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  I communicate digitally rather than over the phone — this lets me serve clients in any language, anywhere in the world, without timezone friction.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-secondary/20 border-t border-border">
        <div className="mx-auto max-w-3xl px-5 md:px-10">
          <h2 className="font-display text-4xl md:text-5xl mb-10 text-center">Frequently asked</h2>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-border">
                <AccordionTrigger className="font-display text-lg md:text-xl text-left hover:text-primary hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
